import { ILogger } from '@common/model/logger';
import { compile } from 'path-to-regexp';

type ParamData = Record<string, string | string[]>;

const cache: Record<string, ReturnType<typeof compile>> = {};
const cacheLimit = 10000;
let cacheCount = 0;

/**
 * Convert path-to-regexp v1 optional param syntax `/:param?` to v8 `{/:param}`.
 * core.server overrides path-to-regexp to 8.x, which rejects `?`.
 */
const toV8OptionalSyntax = (path: string): string => path.replace(/\/:([^/?]+)\?/g, '{/:$1}');

const compilePath = (path: string, options: { logger?: ILogger } = {}): ReturnType<typeof compile> => {
  if (cache[path]) {
    return cache[path];
  }

  let generator: ReturnType<typeof compile>;

  try {
    generator = compile(path);
  } catch (err) {
    const normalizedPath = toV8OptionalSyntax(path);

    if (normalizedPath === path) {
      options.logger?.error(`[common/routing/lib]: Failed to compile path "${path}". Path generating failed.`);
      throw err;
    }

    try {
      generator = compile(normalizedPath);
    } catch (retryErr) {
      options.logger?.error(
        `[common/routing/lib]: Failed to compile path "${path}" (normalized: "${normalizedPath}"). Path generating failed.`,
      );
      throw retryErr;
    }
  }

  if (cacheCount < cacheLimit) {
    cache[path] = generator;
    cacheCount++;
  }

  return generator;
};

/**
 * Public API for generating a URL pathname from a path and parameters.
 */
export const generatePath = (path: string = '/', params: ParamData = {}, options: { logger?: ILogger } = {}): string => {
  return path === '/' ? path : compilePath(path, options)(params);
};
